/**
 * Hooks for accessing files from Supabase Storage
 * Provides two variants for different access patterns:
 * - useStorageSignedUrl: For files that need a signed URL (PDFs, images)
 * - useStorageDownload: For files that need to be downloaded and parsed (CSV, text, HTML)
 *
 * Knowledge-bucket entries hold a viewer's originals (ADR 0091): keyed by actor
 * and organisation, re-read when a preview mounts more than a minute after the
 * last read, dropped a minute after the last viewer unmounts, and refetched
 * when an access change is known (lib/knowledge/evidence-cache-invalidation).
 * A failed read hides the cached result; a background refresh does not.
 */

import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

import { STORAGE_QUERY_KEY } from './pii-query-key-roots';
import { useCacheScope, type CacheScope } from './use-cache-scope';

const DEFAULT_BUCKET = 'knowledge-documents';
export const DEFAULT_EXPIRES_IN = 3600;
/**
 * Refresh the signed URL this many seconds before its JWT exp claim. React
 * Query keeps cached values alive as long as subscribers exist (the
 * quick-draft viewer stays mounted for an entire tab session), so without a
 * periodic refetch a 1 h URL can outlive its lifetime by days and
 * react-pdf is handed an expired token on the next Preview click.
 */
export const SIGNED_URL_REFRESH_BUFFER_S = 60;

/**
 * How long a knowledge-bucket read is reused before a new mount re-reads it,
 * and how long it outlives its last viewer. Focus and reconnect do not re-read
 * it: a new signed URL would reload an open preview, and a URL already minted
 * stays valid until it expires whatever the client does.
 */
export const KNOWLEDGE_REUSE_MS = 60 * 1000;

// =============================================================================
// Query Keys
// =============================================================================

// =============================================================================
// Shared Fetchers (for prefetchQuery / ensureQueryData callers)
// =============================================================================

interface FetchSignedUrlOptions {
  /**
   * When set, the signed URL is minted with `Content-Disposition: attachment;
   * filename=…` so browsers download instead of navigate. Required for
   * cross-origin downloads — without this header the browser ignores the
   * `<a download>` attribute and renders the file in a new tab.
   */
  download?: string;
}

export async function fetchSignedUrl(
  storagePath: string,
  bucket = DEFAULT_BUCKET,
  expiresIn = DEFAULT_EXPIRES_IN,
  options: FetchSignedUrlOptions = {},
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, expiresIn, options);

  if (error) {
    throw new Error(`Failed to get file URL: ${error.message}`);
  }
  if (!data?.signedUrl) {
    throw new Error('No signed URL returned');
  }
  return data.signedUrl;
}

export async function fetchStorageDownload(
  storagePath: string,
  bucket = DEFAULT_BUCKET,
): Promise<{ blob: Blob; text: string }> {
  const { data, error } = await supabase.storage.from(bucket).download(storagePath);

  if (error) {
    throw new Error(`Failed to download file: ${error.message}`);
  }
  if (!data) {
    throw new Error('No file data received');
  }
  return { blob: data, text: await data.text() };
}

/**
 * Whether a cache key holds a knowledge-bucket signed URL or download, whose
 * reads follow the viewer's access. Mirrors the `storageKeys` layout:
 * `['storage', kind, actorId, organizationId, bucket, ...]`.
 */
export function isKnowledgeStorageKey(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === STORAGE_QUERY_KEY && queryKey[4] === DEFAULT_BUCKET;
}

/**
 * Signed URLs and downloaded originals are minted under the requesting
 * viewer's Storage RLS, so their cache entries are keyed by actor and
 * organisation as well as by path (ADR 0091): a path-only key would let one
 * member's URL, or one office's file, be served to the next viewer of the same
 * path, and an organisation switch could not evict exactly the entries that
 * stopped being valid. `scope` comes from `useCacheScope()`.
 */
export const storageKeys = {
  all: [STORAGE_QUERY_KEY] as const,
  signedUrl: (scope: CacheScope, bucket: string, path: string, expiresIn = 3600) =>
    [
      ...storageKeys.all,
      'signed-url',
      scope.actorId,
      scope.organizationId,
      bucket,
      path,
      expiresIn,
    ] as const,
  download: (scope: CacheScope, bucket: string, path: string) =>
    [...storageKeys.all, 'download', scope.actorId, scope.organizationId, bucket, path] as const,
};

// =============================================================================
// Signed URL Hook
// =============================================================================

interface UseStorageSignedUrlOptions {
  bucket?: string;
  expiresIn?: number;
  enabled?: boolean;
}

interface UseStorageSignedUrlResult {
  url: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for getting a signed URL for file access (PDFs, images)
 * Use this when you need to pass a URL to a component that will fetch the file itself.
 * Results are cached for 30 minutes to avoid redundant network requests.
 */
export function useStorageSignedUrl(
  storagePath: string,
  options: UseStorageSignedUrlOptions = {},
): UseStorageSignedUrlResult {
  const { bucket = DEFAULT_BUCKET, expiresIn = DEFAULT_EXPIRES_IN, enabled = true } = options;
  const scope = useCacheScope();

  // Refresh SIGNED_URL_REFRESH_BUFFER_S seconds before the URL would expire
  // so cached values never outlive the JWT. Without this the query survives
  // its 1 h lifetime indefinitely while the tab is open (see buffer comment).
  const refreshMs = Math.max(1, (expiresIn - SIGNED_URL_REFRESH_BUFFER_S) * 1000);

  const isKnowledge = bucket === DEFAULT_BUCKET;
  const { data, isLoading, error } = useQuery({
    queryKey: storageKeys.signedUrl(scope, bucket, storagePath, expiresIn),
    queryFn: async () => await fetchSignedUrl(storagePath, bucket, expiresIn),
    enabled: enabled && Boolean(storagePath),
    // Other buckets: the stale window ends just before the URL expires, so
    // any trigger after that (mount / focus / refetchInterval) fetches a
    // fresh URL. The interval renews a URL shortly before it expires.
    staleTime: isKnowledge ? KNOWLEDGE_REUSE_MS : refreshMs,
    gcTime: isKnowledge ? KNOWLEDGE_REUSE_MS : refreshMs + 60 * 1000,
    // The app default does not refetch on mount; a knowledge read past its
    // reuse window must.
    refetchOnMount: isKnowledge ? true : undefined,
    refetchInterval: refreshMs,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: !isKnowledge,
    refetchOnReconnect: !isKnowledge,
  });

  const errorMessage = error instanceof Error ? error.message : 'Failed to load file';

  return {
    // A failed knowledge read withdraws the cached URL (fail closed).
    url: isKnowledge && error ? null : (data ?? null),
    isLoading,
    error: error ? errorMessage : null,
  };
}

// =============================================================================
// Download Hook
// =============================================================================

interface UseStorageDownloadOptions {
  bucket?: string;
  enabled?: boolean;
  /** Override the default 15-minute gcTime — pass a smaller value for transient previews. */
  gcTime?: number;
}

interface UseStorageDownloadResult {
  data: Blob | null;
  text: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for downloading and parsing text-based files (CSV, text, HTML, DOCX)
 * Use this when you need the actual file content.
 * Default cache: `staleTime` 10 min (refetch boundary), `gcTime` 15 min
 * (eviction when no observers). Callers with transient access patterns
 * (sidebar previews) should pass `gcTime: 5 * 60 * 1000` to keep cache
 * memory bounded.
 */
export function useStorageDownload(
  storagePath: string,
  options: UseStorageDownloadOptions = {},
): UseStorageDownloadResult {
  const { bucket = DEFAULT_BUCKET, enabled = true, gcTime = 15 * 60 * 1000 } = options;
  const scope = useCacheScope();

  const isKnowledge = bucket === DEFAULT_BUCKET;
  const { data, isLoading, error } = useQuery({
    queryKey: storageKeys.download(scope, bucket, storagePath),
    queryFn: async () => await fetchStorageDownload(storagePath, bucket),
    enabled: enabled && Boolean(storagePath),
    staleTime: isKnowledge ? KNOWLEDGE_REUSE_MS : 10 * 60 * 1000,
    gcTime: isKnowledge ? KNOWLEDGE_REUSE_MS : gcTime,
    ...(isKnowledge
      ? { refetchOnMount: true, refetchOnWindowFocus: false, refetchOnReconnect: false }
      : {}),
  });

  const errorMessage = error instanceof Error ? error.message : 'Failed to load file';

  return {
    data: isKnowledge && error ? null : (data?.blob ?? null),
    text: isKnowledge && error ? null : (data?.text ?? null),
    isLoading,
    error: error ? errorMessage : null,
  };
}
